import { createContext, useMemo, useEffect, useRef, useState, useContext } from "react";
import UserContext from "./user";

const cmsLanguages = [
  { value: "en-us", label: "English", profile: "en" },
  { value: "it-it", label: "Italiano", profile: "it" },
  { value: "fr-fr", label: "Français", profile: "fr" },
  { value: "es-es", label: "Español", profile: "es" },
  { value: "pt-br", label: "Português", profile: "pt" },
];

const tamTamyLanguagesMap = {
  en: "en_US",
  it: "it_IT",
  fr: "fr_FR",
  es: "es_ES",
  pt: "pt_PT",
};

const LanguageContext = createContext({
  language: "en",
  defaultlabels: {},
  cmsLang: "en-us",
  browserLanguage: "en",
  unloggedLanguage: "en",
  cmsLanguages,
  tamTamyLanguagesMap,
  languages: [],
});

export default LanguageContext;

const LABELS_KEY = "labels";

export const LanguageProvider = ({ children }) => {
  const [defaultLabels, setDefaultLabels] = useState(
    JSON.parse(localStorage.getItem(LABELS_KEY) || "{}")
  );
  const loadedLabels = useRef(false);
  const { logged, userProfile } = useContext(UserContext);
  const language = userProfile?.language
    ? cmsLanguages?.find((lang) => lang.profile === userProfile.language)?.profile || "en"
    : null;
  const cmsLang = language
    ? cmsLanguages?.find((lang) => lang.profile === language)?.value || "en-us"
    : null;
  const [browserLanguage, setBrowserLanguage] = useState();
  const rawBroswerLanguage = (navigator?.language || navigator?.userLanguage || "en-us")?.split(
    "-"
  )[0];
  const unloggedLanguage = useMemo(
    () => localStorage.getItem("lang-interface") || browserLanguage,
    [browserLanguage]
  );

  const [languages, setLanguages] = useState();
  const languagesRef = useRef();

  useEffect(() => {
    if (!languagesRef.current) {
      const loadLanguages = async () => {
        languagesRef.current = true;
        try {
          const response = await fetch("/bff/translation/language", {
            headers: {
              Accept: "*/*",
            },
          });
          if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
          }
          const result = await response.json();
          const formattedResult = result.map(({ code, description }) => {
            return { value: code, label: description };
          });
          setLanguages(formattedResult);
          setBrowserLanguage(
            formattedResult?.find((lang) => lang.value === rawBroswerLanguage)?.value || "en"
          );
        } catch (err) {
          languagesRef.current = false;
          console.error("Err: ", err);
        }
      };
      loadLanguages();
    }
  }, [rawBroswerLanguage]);

  useEffect(() => {
    if (!loadedLabels.current) {
      const getLabels = async () => {
        loadedLabels.current = true;
        try {
          const response = await fetch(`/bff/translation?locale=en`, {
            headers: {
              Accept: "*/*",
            },
          });
          if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
          }
          const result = await response.json();
          setDefaultLabels(
            Object.fromEntries(
              Object.entries(result)?.filter(
                ([key, value]) => !key.startsWith("MENU_") && value !== "-"
              )
            )
          );
        } catch (err) {
          console.error(err);
        }
      };
      getLabels();
    }
  }, [logged, language, unloggedLanguage]);

  const contextValue = useMemo(
    () => ({
      defaultLabels,
      cmsLang,
      browserLanguage,
      unloggedLanguage,
      cmsLanguages,
      tamTamyLanguagesMap,
      language,
      languages,
    }),
    [defaultLabels, cmsLang, browserLanguage, unloggedLanguage, language, languages]
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
